import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Reveal, ClipReveal } from '@/components/site/Reveal';
import { WordReveal } from '@/components/site/WordReveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CTABand } from '@/components/site/CTABand';
import { WRAP, SECTION, H2, H2_SM } from '@/components/site/ServicePageParts';
import { WORK } from '@/data/work';
import WORK_SHOTS from '@/data/workShots.json';

const EASE = '[transition-timing-function:var(--ease-out)]';

function Shot({ shot, name, eager, className = '' }) {
  if (shot.mobile) {
    return (
      <figure className={`w-[min(240px,70vw)] shrink-0 ${className}`}>
        <div className="overflow-hidden rounded-[28px] ring-1 ring-white/[0.14] bg-ink-bg shadow-[0_40px_80px_-30px_rgba(5,5,12,.95)]">
          <img
            src={shot.src}
            alt={`${name}: ${shot.label}`}
            width={shot.width}
            height={shot.height}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            className="block w-full h-auto"
          />
        </div>
        <figcaption className="nd-mono mt-4 !text-[11px] text-center">{shot.label}</figcaption>
      </figure>
    );
  }
  return (
    <figure className={`group w-full ${className}`}>
      <ClipReveal className="overflow-hidden rounded-[18px] ring-1 ring-white/[0.08] bg-ink-surface">
        <img
          src={shot.src}
          alt={`${name}: ${shot.label}`}
          width={shot.width}
          height={shot.height}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className={`block w-full h-auto transition-transform [transition-duration:1100ms] ${EASE} group-hover:scale-[1.02]`}
        />
      </ClipReveal>
      <figcaption className="mt-4 flex items-baseline justify-between gap-6">
        <span className="text-sm text-[#C9C9D3]">{shot.label}</span>
        <span className="nd-mono !text-[11px] hidden sm:inline">{name}</span>
      </figcaption>
    </figure>
  );
}

export default function WorkDetail({ slug }) {
  const w = WORK.find((p) => p.slug === slug);
  const shots = WORK_SHOTS[slug] || [];
  const desktop = shots.filter((s) => !s.mobile);
  const mobile = shots.filter((s) => s.mobile);
  const others = WORK.filter((p) => p.slug !== slug);
  const idx = WORK.findIndex((p) => p.slug === slug);
  const next = WORK[(idx + 1) % WORK.length];
  const [galleryLead, ...galleryRest] = desktop.slice(1);
  const facts = [
    ['Sector', w.sector],
    ['What we built', w.kind],
    ['Services', w.services.join(' · ')],
    ['Status', 'Live'],
  ];

  return (
    <div data-testid={`work-detail-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
          style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
        />
        <div className={`relative ${WRAP} pt-16 md:pt-24 pb-14 md:pb-20`}>
          <nav aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span aria-hidden>/</span>
            <Link to="/work/" className="hover:text-white transition-colors">Work</Link>
            <span aria-hidden>/</span>
            <span className="text-white">{w.name}</span>
          </nav>

          <SectionLabel as="h1" num={String(idx + 1).padStart(2, '0')} className="mt-12 md:mt-16" data-testid={`work-detail-${slug}-h1`}>
            {w.h1}
          </SectionLabel>
          <WordReveal
            as="p"
            text={w.name}
            className="mt-8 font-display font-semibold text-[clamp(56px,11vw,200px)] leading-[0.88] tracking-[-0.055em]"
          />

          <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <Reveal delay={300} className="lg:col-span-7">
              <p className="font-display font-medium text-[clamp(22px,2.2vw,34px)] leading-[1.2] tracking-[-0.03em] text-[#C9C9D3] max-w-[34ch] text-balance">
                {w.summary}
              </p>
            </Reveal>
            <Reveal delay={400} className="lg:col-span-5 flex flex-wrap items-center gap-3 lg:justify-end">
              <MagneticButton href={w.url} external variant="primary">
                Visit {w.domain} <ArrowUpRight size={16} />
              </MagneticButton>
              <MagneticButton to="/contact/" variant="ghost">Discuss a similar build</MagneticButton>
            </Reveal>
          </div>

          <Reveal delay={450}>
            <dl className="mt-14 md:mt-20 grid grid-cols-2 lg:grid-cols-5 border-t border-white/[0.08]">
              {facts.map(([k, v], i) => (
                <div key={k} className={`pt-5 pb-2 pr-4 border-white/[0.08] ${i % 2 ? 'pl-5 border-l' : ''} ${i > 1 ? 'max-lg:mt-5 max-lg:border-t' : ''} ${i === 2 ? 'lg:pl-5 lg:border-l' : ''}`}>
                  <dt className="nd-mono !text-[11px]">{k}</dt>
                  <dd className="mt-2.5 text-[15px] text-white/90 leading-snug flex items-center gap-2">
                    {k === 'Status' && <span className="nd-live" />}
                    {v}
                  </dd>
                </div>
              ))}
              <div className="col-span-2 lg:col-span-1 pt-5 pb-2 border-white/[0.08] max-lg:mt-5 max-lg:border-t lg:pl-5 lg:border-l">
                <dt className="nd-mono !text-[11px]">Live URL</dt>
                <dd className="mt-2.5 text-[15px]">
                  <a href={w.url} target="_blank" rel="noopener noreferrer" className="nd-link-u text-white">
                    {w.domain} <span aria-hidden>↗</span>
                  </a>
                </dd>
              </div>
            </dl>
          </Reveal>
        </div>
      </section>

      {/* HERO SHOT — near full-bleed */}
      {desktop[0] && (
        <section className="relative pb-[clamp(72px,9vw,140px)]">
          <div className="mx-auto max-w-[1680px] px-4 sm:px-6">
            <Shot shot={desktop[0]} name={w.name} eager />
          </div>
        </section>
      )}

      {/* 01 THE PROJECT */}
      <section className={SECTION}>
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8`}>
          <SectionLabel num="01" className="lg:pt-4">The project</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-5">
              <h2 className={H2_SM}>What {w.name} is</h2>
            </Reveal>
            <div className="lg:col-span-7 space-y-6">
              {w.overview.map((para, i) => (
                <Reveal key={i} delay={i * 80}>
                  <p className={`leading-relaxed ${i === 0 ? 'text-[clamp(19px,1.5vw,22px)] text-[#C9C9D3]' : 'text-[17px] text-ink-muted'}`}>{para}</p>
                </Reveal>
              ))}
              {w.note && <p className="nd-mono !text-[11px] !normal-case !tracking-normal pt-2">{w.note}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* 02 WHAT WE BUILT — ruled k / v rows */}
      <section className={SECTION}>
        <div className={WRAP}>
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
            <SectionLabel num="02" className="lg:pt-4">What we built</SectionLabel>
            <Reveal>
              <h2 className={`${H2} max-w-[18ch]`}>Features in <span className="nd-accent">{w.name}</span></h2>
            </Reveal>
          </div>
          <ol className="border-t border-white/[0.08]">
            {w.built.map((b, i) => (
              <Reveal as="li" key={b.k} delay={(i % 3) * 60} className="border-b border-white/[0.08]">
                <div className="group relative isolate grid grid-cols-[48px_1fr] lg:grid-cols-[220px_1fr_1.25fr] gap-x-8 gap-y-3 py-8 md:py-10">
                  <span aria-hidden className={`absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.10),rgba(75,159,255,.03))] transition-transform [transition-duration:550ms] ${EASE} group-hover:scale-y-100`} />
                  <span className="nd-mono pt-2">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className={`font-display text-2xl md:text-[34px] font-semibold tracking-[-0.035em] leading-tight transition-transform [transition-duration:550ms] ${EASE} lg:group-hover:translate-x-3`}>{b.k}</h3>
                  <p className="col-start-2 lg:col-start-3 text-ink-muted leading-relaxed max-w-[54ch] lg:pt-1.5">{b.v}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* 03 SCREENSHOTS */}
      <section data-testid="work-screenshots" className={SECTION}>
        <div className={WRAP}>
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
            <SectionLabel num="03" className="lg:pt-4">Screenshots</SectionLabel>
            <div>
              <Reveal>
                <h2 className={H2_SM}>{w.name} screenshots</h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="mt-6 text-ink-muted text-[17px] max-w-2xl">
                  Captured from the live product at{' '}
                  <a href={w.url} target="_blank" rel="noopener" className="nd-link-u text-white">
                    {w.domain}
                  </a>
                  .
                </p>
              </Reveal>
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-[1680px] px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-[clamp(48px,6vw,96px)]">
          {galleryLead && (
            <Shot shot={galleryLead} name={w.name} className={mobile.length ? 'lg:col-span-8' : 'lg:col-span-12'} />
          )}
          {mobile.length > 0 && (
            <div className={`${galleryLead ? 'lg:col-span-4' : 'lg:col-span-12'} relative rounded-[18px] ring-1 ring-white/[0.08] bg-ink-surface overflow-hidden flex flex-wrap items-center justify-center gap-8 px-6 py-12 lg:self-start`}>
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{ background: `radial-gradient(circle at 50% 30%, ${w.palette[0]}40, transparent 65%)` }}
              />
              {mobile.map((s) => (
                <Shot key={s.src} shot={s} name={w.name} className="relative" />
              ))}
            </div>
          )}
          {galleryRest.map((s) => (
            <Shot key={s.src} shot={s} name={w.name} className="lg:col-span-12" />
          ))}
        </div>
      </section>

      {/* 04 STACK + SERVICES */}
      <section className={SECTION}>
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-2 gap-[clamp(56px,7vw,120px)]`}>
          <div>
            <SectionLabel num="04">Technology</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}>Stack and platform</h2></Reveal>
            <Reveal delay={100} className="mt-8 flex flex-wrap gap-2">
              {w.stack.map((x) => (
                <span key={x} className="font-mono text-[12px] tracking-[.04em] text-[#C9C9D3] px-3 py-2 border border-white/[0.14] rounded-md">{x}</span>
              ))}
            </Reveal>
          </div>
          <div>
            <SectionLabel num="04">Services</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}>Services used on this project</h2></Reveal>
            <ul className="mt-8 border-t border-white/[0.08]">
              {w.serviceLinks.map((l) => (
                <li key={l.to} className="border-b border-white/[0.08]">
                  <Link to={l.to} className="arrow-link group flex items-center justify-between gap-4 py-4 text-[#C9C9D3] hover:text-white transition-colors">
                    <span className={`font-display text-lg tracking-[-0.02em] transition-transform [transition-duration:550ms] ${EASE} group-hover:translate-x-2`}>{l.label}</span>
                    <span className="arrow text-ink-muted group-hover:text-white">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 05 MORE WORK + NEXT PROJECT */}
      <section className={SECTION}>
        <div className={WRAP}>
          <SectionLabel num="05">More work</SectionLabel>
          <ul className="mt-10 grid grid-cols-2 lg:grid-cols-4 border-t border-white/[0.08]">
            {others.map((p, i) => (
              <li key={p.slug} className={`border-b border-white/[0.08] ${i % 2 ? 'border-l pl-5' : 'pr-5'} ${i > 0 && i % 2 === 0 ? 'lg:border-l lg:pl-5' : ''}`}>
                <Link to={`/work/${p.slug}/`} className="group block py-6">
                  <div className="nd-mono !text-[11px]">{p.sector}</div>
                  <div className={`mt-3 font-display text-xl md:text-2xl font-semibold tracking-[-0.03em] transition-transform [transition-duration:550ms] ${EASE} group-hover:translate-x-2`}>{p.name}</div>
                  <span className="mt-4 nd-link-u text-sm text-white">Case study →</span>
                </Link>
              </li>
            ))}
          </ul>

          {next && next.slug !== slug && (
            <Link
              to={`/work/${next.slug}/`}
              data-cursor="View"
              className="group mt-[clamp(72px,9vw,140px)] grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-end"
            >
              <div>
                <div className="nd-mono">Next project — {next.sector}</div>
                <div className={`mt-5 font-display font-semibold text-[clamp(56px,10vw,180px)] leading-[0.88] tracking-[-0.055em] transition-transform [transition-duration:700ms] ${EASE} group-hover:translate-x-4`}>
                  {next.name}
                </div>
              </div>
              <span aria-hidden className="h-16 w-16 md:h-24 md:w-24 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white">
                <svg viewBox="0 0 16 16" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.4" className={`transition-transform [transition-duration:550ms] ${EASE} group-hover:-rotate-45`}><path d="M2 8h12M9 3l5 5-5 5" /></svg>
              </span>
            </Link>
          )}
        </div>
      </section>

      <CTABand
        eyebrow="Want something like this?"
        headline={`Let’s talk about your version of ${w.name}.`}
        accent={`${w.name}.`}
        sub="A free 30-minute call on WhatsApp. You get a fixed written quote."
      />
    </div>
  );
}
